"use client";

import { Popover } from "@typeform/embed-react";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { safeSetLocalStorage } from "scmp-sdk";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { theme } from "shared/lib/styles";
import { parseQueryString } from "shared/lib/utils";

import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";

import type { TypeformConfigKey } from "./consts";
import { TypeformConfigMap } from "./consts";

type Props = {
  configKey?: TypeformConfigKey;
  utm_content?: string;
  utm_source?: null | string;
  utm_term?: string;
};

export const TypeformComponent: FunctionComponent<Props> = (props: Props) => {
  const { configKey, utm_content, utm_source, utm_term } = props;
  const config = configKey ? TypeformConfigMap[configKey] : undefined;

  const { user } = useAtomValue(accountAtom);
  const isShowTakeoverAdSlot = Boolean(useAtomValue(isShowTakeoverAdSlotAtom));
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { query } = useRouter();
  const searchQuery = useMemo(() => parseQueryString(query.q) ?? "", [query.q]);

  const [isShowPopover, setIsShowPopover] = useState(false);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

  useEffect(() => {
    if (!config?.key || localStorage?.getItem(config?.key) === "1") return;

    setIsShowPopover(true);

    const shouldAutoOpenForDevice = isMobile
      ? config.shouldAutoOpen.mobile
      : config.shouldAutoOpen.desktop;

    if (shouldAutoOpenForDevice) {
      const timer = setTimeout(() => {
        setShouldAutoOpen(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile, config?.key, config?.shouldAutoOpen.desktop, config?.shouldAutoOpen.mobile]);

  const onFormFinished = () => {
    safeSetLocalStorage(config!.key, "1");
  };

  const onReady = () => {
    if (config?.treatAsFinished === "onReady") {
      onFormFinished();
    }
  };

  const onSubmit = () => {
    if (config?.treatAsFinished === "onSubmit") {
      onFormFinished();
    }
  };

  const hiddenFields = useMemo(
    () => ({
      utm_content: utm_content ?? searchQuery,
      ...(utm_source !== null && { utm_source: utm_source ?? (isMobile ? "mdot" : "desk") }),
      utm_term: utm_term ?? user?.uuid ?? "",
    }),
    [utm_content, utm_source, utm_term, isMobile, searchQuery, user?.uuid],
  );

  const onClose = () => {
    setIsShowPopover(false);
  };

  const takeoverOffset = isDesktopUp ? "96px" : "56px";

  const customCss = useMemo(
    () => `
      .tf-v1-popover-button {
        ${isShowTakeoverAdSlot ? `bottom: ${takeoverOffset} !important;` : ""}
        background-color: #ffcb05 !important;
        svg {
          path {
            fill: #001246;
          }
        }
      }
    `,
    [isShowTakeoverAdSlot, takeoverOffset],
  );

  if (!isShowPopover || !config?.id) return null;
  return (
    <ErrorBoundary fallback={<></>}>
      <ClientSideSuspense fallback={<></>}>
        <style>{customCss}</style>
        <Popover
          height={600}
          hidden={hiddenFields}
          hideHeaders
          id={config.id}
          medium="popover"
          onClose={onClose}
          onReady={onReady}
          onSubmit={onSubmit}
          open={shouldAutoOpen ? "load" : undefined}
        />
      </ClientSideSuspense>
    </ErrorBoundary>
  );
};

export const Typeform = memo(TypeformComponent);
